import { ResponseStatusBadge } from '../../ResponseStatusBadge';
import { Text } from '../../Text';
import { useMemo } from 'react';
import { TChannel } from '../../../api/messages';
import { useMessageStore } from '../../../store';
import { commonStrings, messageStrings } from '../../../constants/strings';
import { Avatar } from 'components/Avatar';
import { useCurrentUser } from 'hooks';
import {
  CHAT_FILE_SENT_LABEL,
  getChatFileKind,
} from '../../Chat/constants';

type Props = {
  channel: TChannel & {
    lastMessage?: {
      channel: string;
      message: any;
      timetoken: string | number;
      uuid?: string;
      recalled?: boolean;
    };
  };
  onSelectChannel: (id: string) => void;
  isActive?: boolean;
  unreadCount?: number;
};

export function ChannelListItem({
  channel,
  onSelectChannel,
  isActive = false,
  unreadCount = 0,
}: Props) {
  const { data: currentUser } = useCurrentUser();
  const { draftMessages, draftFiles } = useMessageStore();

  // Filter participants to exclude current user and get first member
  const userMale = useMemo(
    () => channel.participants.find((item) => item.uuid !== currentUser?.uuid),
    [channel.participants, currentUser?.uuid],
  );

  const [draftText, draftFile] = useMemo(
    () => [draftMessages?.[channel.id], draftFiles?.[channel.id]],
    [channel.id, draftFiles, draftMessages],
  );
  const draftFileCount = draftFile?.length;
  const draftFileType = draftFile?.[0]?.type;

  const messageView = useMemo(() => {
    if (channel.lastMessage?.recalled) {
      return messageStrings.recalledMessagePreview;
    }

    const lastMessage =
      channel.lastMessage?.message?.message || channel.lastMessage?.message;
    const text = lastMessage?.text;

    // Check for PubNub file
    const hasFile = channel.lastMessage?.message?.file;
    // Check for media array (from backend sendMessageFromAdmin)
    const hasMedia =
      lastMessage?.media &&
      Array.isArray(lastMessage.media) &&
      lastMessage.media.length > 0;
    // hasFile（PubNub ファイル）は常に画像。media がある場合のみ型で種別を判定する
    const mediaType = hasMedia ? lastMessage.media[0]?.type : undefined;

    const file =
      hasFile || hasMedia
        ? CHAT_FILE_SENT_LABEL[getChatFileKind(mediaType)]
        : undefined;
    const message = text || file || '';

    const draftMessage =
      draftText?.message ||
      (draftFileCount
        ? CHAT_FILE_SENT_LABEL[getChatFileKind(draftFileType)]
        : undefined) ||
      message;

    return !!unreadCount ? message : draftMessage;
  }, [
    channel.lastMessage,
    draftFileCount,
    // 添付を画像→PDFに差し替えたときもラベルを更新するため、種別も依存に含める
    draftFileType,
    draftText?.message,
    unreadCount,
  ]);

  const avatarUrl = userMale?.avatar;

  return (
    <div
      className={`flex items-center h-full w-full gap-3 border-b border-neutral-200 p-3 sm:p-4 cursor-pointer
        ${isActive ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
      onClick={() => onSelectChannel(channel.id)}
    >
      <div className="place-self-center flex-shrink-0">
        <Avatar
          url={avatarUrl}
          size="h-12 w-12 sm:h-14 sm:w-14"
          className="rounded-full border border-neutral-200"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <Text className="line-clamp-1 break-all min-w-0" bold>
            {userMale && 'userLineId' in userMale
              ? `${userMale.name} (${userMale.userLineId})`
              : userMale?.name}
          </Text>
          {channel.kind === 'MALE' && (
            <ResponseStatusBadge status={channel.responseStatus} />
          )}
        </div>

        <div className="flex mt-2 justify-between items-center">
          <div className="flex-1 line-clamp-1 break-all text-neutral-600 text-xs min-w-0">
            {(draftText || draftFile) && !unreadCount && (
              <span className="text-primary">
                {commonStrings.draft}:{' '}
              </span>
            )}
            {messageView}
          </div>

          {!!unreadCount && (
            <div className="relative w-5 h-5 bg-red-500 rounded-full ml-2 flex-shrink-0">
              <Text
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                textColor="text-white"
                textSize="text-[11px]"
              >
                {unreadCount > 9 ? `9+` : unreadCount}
              </Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
